import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Search, X } from "lucide-react";
import client from "../api/client";
import "./Admin.css";

const TABS = [
  { key: "requests", label: "Requests", countKey: "pending" },
  { key: "users", label: "All Users", countKey: "approved" },
];

const USER_TYPE_LABELS = {
  architect: "Architect",
  interior: "Interior Designer",
  firm: "Firm",
};

const formatTime = (value) => (value ? new Date(value).toLocaleString() : "—");
const typeLabel = (value) => USER_TYPE_LABELS[value] || value || "—";

export default function Users() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "users" ? "users" : "requests";

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/api/admin/users");
      setItems(data.items);
      setCounts(data.counts);
      setError("");
    } catch (err) {
      console.error("Users failed:", err);
      setError("Could not load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectTab = (key) => {
    setQuery("");
    setMessage("");
    setSearchParams(key === "users" ? { tab: "users" } : {}, { replace: true });
  };

  const visible = useMemo(() => {
    const status = tab === "requests" ? "pending" : "approved";
    const needle = query.trim().toLowerCase();
    return items.filter(
      (user) =>
        user.status === status &&
        (!needle ||
          [user.name, user.email, user.phone_number, user.firm_name, user.address]
            .filter(Boolean)
            .some((field) => field.toLowerCase().includes(needle)))
    );
  }, [items, tab, query]);

  const review = async (user, action) => {
    if (
      action === "reject" &&
      !window.confirm(
        `Reject the request from ${user.name}? They will be notified by email and can apply again.`
      )
    ) {
      return;
    }

    setBusyId(user.user_id);
    setMessage("");
    setError("");
    try {
      await client.post(`/api/admin/users/${user.user_id}/${action}`);
      setMessage(
        action === "approve"
          ? `${user.name} has been approved and notified by email.`
          : `The request from ${user.name} has been rejected.`
      );
      await load();
    } catch (err) {
      console.error(`${action} failed:`, err);
      setError(err.response?.data?.error || `Could not ${action} this request.`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div>
          <p className="admin-kicker">Administration</p>
          <h1>Users</h1>
        </div>
        <label className="admin-search">
          <Search size={15} strokeWidth={1.8} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search name, email, phone"
            aria-label="Search users"
          />
        </label>
      </header>

      <div className="admin-tabs" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            className={tab === item.key ? "active" : ""}
            onClick={() => selectTab(item.key)}
          >
            {item.label}
            <span className="admin-tab-count">{counts[item.countKey] ?? 0}</span>
          </button>
        ))}
      </div>

      {message && <p className="admin-notice">{message}</p>}
      {error && <p className="admin-error" role="alert">{error}</p>}

      {loading ? (
        <p>Loading users...</p>
      ) : (
        <section className="admin-panel" role="tabpanel">
          <div className="admin-table-scroll">
            {tab === "requests" ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Requested</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Type</th>
                    <th>Firm</th>
                    <th>Location</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((user) => (
                    <tr key={user.user_id}>
                      <td>{formatTime(user.created_at)}</td>
                      <td className="cell-strong">{user.name}</td>
                      <td>{user.email || "—"}</td>
                      <td>{user.phone_number}</td>
                      <td>{typeLabel(user.user_type)}</td>
                      <td>{user.firm_name || "—"}</td>
                      <td>{user.address || "—"}</td>
                      <td>
                        <div className="admin-row-actions">
                          <button
                            type="button"
                            className="approve"
                            onClick={() => review(user, "approve")}
                            disabled={busyId === user.user_id}
                          >
                            <Check size={14} strokeWidth={2} aria-hidden="true" />
                            Approve
                          </button>
                          <button
                            type="button"
                            className="reject"
                            onClick={() => review(user, "reject")}
                            disabled={busyId === user.user_id}
                          >
                            <X size={14} strokeWidth={2} aria-hidden="true" />
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!visible.length && (
                    <tr>
                      <td colSpan={8} className="admin-empty-cell">
                        {query ? "No requests match your search." : "No pending requests."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Type</th>
                    <th>Firm</th>
                    <th>Location</th>
                    <th>Role</th>
                    <th>Approved</th>
                    <th>Last login</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((user) => (
                    <tr key={user.user_id}>
                      <td className="cell-strong">{user.name}</td>
                      <td>{user.email || "—"}</td>
                      <td>{user.phone_number}</td>
                      <td>{typeLabel(user.user_type)}</td>
                      <td>{user.firm_name || "—"}</td>
                      <td>{user.address || "—"}</td>
                      <td>
                        <span className={`pill ${user.role === "Admin" ? "pill-running" : "pill-skipped"}`}>
                          {user.role || "—"}
                        </span>
                      </td>
                      <td>{formatTime(user.approved_at)}</td>
                      <td>{formatTime(user.last_login)}</td>
                    </tr>
                  ))}
                  {!visible.length && (
                    <tr>
                      <td colSpan={9} className="admin-empty-cell">
                        {query ? "No users match your search." : "No users yet."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}
    </main>
  );
}
